/** Notifications feed (v1 analytics.js loadNotifs / _markRead). The feed is
 * server-backed and SHARED with v1 — so during the parallel run v2 surfaces the
 * same price-drop / new-lead / task rows v1 emits. Reads are safe; mark-read is
 * non-destructive (flips your own read flags), so it isn't liveFire-gated. */
import { backendFetch } from "../../lib/backend";

export interface Notif {
  id: string;
  ev: string; // event type (priceDrop, newLead, task, …)
  title: string;
  body: string;
  link: string; // "detail:<addr>", "task:*", "wholesale", …
  read: boolean;
  at: string; // created_at ISO
}

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

export function narrowNotif(raw: unknown): Notif {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    id: str(o["id"]),
    ev: str(o["ev"]),
    title: str(o["title"]),
    body: str(o["body"]),
    link: str(o["link"]),
    read: o["read"] === true,
    at: str(o["created_at"] ?? o["at"]),
  };
}

export async function fetchNotifications(token: string | null, limit = 50, signal?: AbortSignal): Promise<Notif[]> {
  const j = await backendFetch(token, `/api/notifications?limit=${limit}`, { timeoutMs: 30000, signal });
  const o = j && typeof j === "object" ? (j as Record<string, unknown>) : {};
  const rows = Array.isArray(o["notifications"]) ? o["notifications"] : [];
  return rows.map(narrowNotif).filter((n) => n.id !== "");
}

/** Emit a client-detected event (POST /api/notifications/emit, already live).
 *
 * v2 was read-only on this feed, so assigning a task to a teammate here told
 * nobody — v1 has always emitted `taskAssigned` on create (transactions.js
 * saveTaskModal → notifyUser). `to_users` are Supabase profile ids; the server
 * validates them against the CALLER'S org, so a bad id notifies nobody rather
 * than a stranger. Fire-and-forget: a notification must never fail a save. */
export async function emitNotif(
  token: string | null,
  req: { ev: string; title: string; body?: string; link?: string; dedupe_key?: string; to_users?: string[] },
): Promise<void> {
  await backendFetch(token, "/api/notifications/emit", { method: "POST", body: req, timeoutMs: 20000 });
}

/** Mark specific rows read, or all (v1 _markRead / markAllRead). Non-destructive. */
export async function markRead(token: string | null, which: { ids: string[] } | { all: true }): Promise<void> {
  await backendFetch(token, "/api/notifications/read", { method: "POST", body: which, timeoutMs: 20000 });
}

/** v1 openNotifLink: map a link string to a v2 nav action. */
export type NotifRoute =
  | { kind: "lead"; address: string }
  | { kind: "convo"; phone: string }
  | { kind: "page"; page: string };
// links that mean "an inbound text/call" — open the CONVERSATION, not a page.
const _CONVO_KINDS = new Set(["dialer", "message", "text", "chat", "sms", "convo", "inbox", "comms"]);
export function notifRoute(link: string): NotifRoute {
  if (!link) return { kind: "page", page: "dashboard" };
  const i = link.indexOf(":");
  const kind = i < 0 ? link : link.slice(0, i);
  const arg = i < 0 ? "" : link.slice(i + 1);
  if (kind === "detail" && arg) return { kind: "lead", address: arg };
  // Inbound text/call (Tom 2026-08-14: "clicking a message notification doesn't
  // go to the message"). The backend links these as "dialer:<phone>", which was
  // mapped to the Contacts PAGE and never opened the thread. Route to the Inbox
  // conversation for that number; with no number, just the Inbox.
  if (_CONVO_KINDS.has(kind)) {
    return arg ? { kind: "convo", phone: arg } : { kind: "page", page: "inbox" };
  }
  if (kind === "task") return { kind: "page", page: "tasks" };
  if (kind === "wholesaledetail" || kind === "wholesale") return { kind: "page", page: "wholesale" };
  if (kind === "txndetail" || kind === "transactions" || kind === "deal") return { kind: "page", page: "deals" };
  if (kind === "pricedrop") return { kind: "page", page: "pricedrops" };
  if (kind === "contact") return { kind: "page", page: "contacts" };
  if (kind === "calendar") return { kind: "page", page: "calendar" };
  if (kind === "notifications") return { kind: "page", page: "notifications" };
  return { kind: "page", page: "dashboard" };
}

/** Event → emoji + hue for the row (v1 used lucide icons per ev). */
export function notifFace(ev: string): { icon: string; hue: string } {
  const e = ev.toLowerCase();
  if (e.includes("digest")) return { icon: "☀️", hue: "amber" };
  if (e.includes("showing")) return { icon: "📅", hue: "violet" };
  if (e.includes("closing")) return { icon: "⏰", hue: "amber" };
  if (e.includes("price")) return { icon: "📉", hue: "amber" };
  if (e.includes("comingsoon")) return { icon: "🔜", hue: "emerald" };
  if (e.includes("lead") || e.includes("deal")) return { icon: "🏠", hue: "emerald" };
  if (e.includes("task")) return { icon: "✓", hue: "blue" };
  if (e.includes("message") || e.includes("text") || e.includes("chat")) return { icon: "💬", hue: "blue" };
  return { icon: "🔔", hue: "slate" };
}
